/**
 * 「送付」を押したときの短い確認（#220、docs/88 §4.2・Figma ②改2。#119 の「メールで送信」の窓を改めた）。メールだけ。
 *   送信先・送信方法・添付の数・写真台帳の有無を2行で見せ、「送信」「取消」。送信方法・添付・印字項目を変えるのは作成画面の帯（送信設定）で、ここでは変えない。
 *   宛先は帯の送信先（先方担当者）のアドレス。アドレスが無いときだけ欄を出す（保存しない）。件名・本文は「メール本文 ⌄」を開くとこの1回だけ修正できる。
 *   送れなければ（10MB を超える・1日 50通・メールサーバー）窓に文を出す。下書きは番号を採らずに下書きのまま
 *   送ったあとの「メールで送信…」（もう一度送る）も同じ窓（題は「メールで送信」）
 *   メール送付（#225、docs/91 §3.8）：窓に「渡し方」（閲覧ページ｜添付。{vendor}ごとの既定 → 添付）と、閲覧ページなら公開期限（日）。送ったメールは送付の記録にも残る
 *   2026-10-21 射場さんの決め（docs/89 ★16）：印刷・FAX と、メールで送付状がオンのときは、この窓を出さずに送付の画面 /rfqs/:id/send（RfqSend.tsx）へ。
 *   窓の「送付状 ⌄」と「番号を採って送付済にします。開いた PDF を印刷して送ってください。」の1行はやめた
 */
import { MAIL_LIMITS, MAIL_MODE_LABEL, MAIL_MSG, RFQ_MSG, RfqSendRequest, addDaysYmd, type MailMode, type Rfq } from "@genbaryoku/contract";
import { useState } from "react";
import { api, messageOf } from "../../api.js";
import { NumInput } from "../../components/NumInput.jsx";
import { Segmented } from "../../components/Segmented.jsx";
import { ErrorText, Field, Help } from "../../components/ui.jsx";
import { todayJst } from "../../lib/num.js";

import { ymdShort } from "../../lib/dates.js";
export function SendDialog({
  rfq,
  bcc,
  title,
  onClose,
  onSent,
  beforeSend,
  busy: outerBusy = false,
}: {
  rfq: Rfq;
  /** 帯の「控え Bcc」 */
  bcc: boolean;
  /** 窓の題（開いたボタンと同じ言葉＝「送付」／「メールで送信」） */
  title: string;
  onClose: () => void;
  onSent: (r: Rfq) => void;
  /** 下書きの書きかけを送り終えるのを待つ。保存できていなければ理由の文（送らない。#177） */
  beforeSend: () => Promise<string | null>;
  busy?: boolean;
}) {
  const known = rfq.contact?.email ?? rfq.mail?.to ?? rfq.contacts.find((c) => c.email)?.email ?? "";
  const [to, setTo] = useState(known);
  const [subject, setSubject] = useState(rfq.mail?.subject ?? "");
  const [body, setBody] = useState(rfq.mail?.body ?? "");
  const [mailOpen, setMailOpen] = useState(false);
  const [mode, setMode] = useState<MailMode>(rfq.mail?.mode ?? "attach");
  const [expiresDays, setExpiresDays] = useState<number>(MAIL_LIMITS.expiresDefault);
  const [err, setErr] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const who = rfq.contact ? `${rfq.vendor.shortName}　${[rfq.contact.department, rfq.contact.name].filter(Boolean).join(" ")} 様` : `${rfq.vendor.shortName} 御中`;
  const n = rfq.attachments.length;
  // 写真台帳：写真のある依頼でスイッチがオンのときだけ「あり」（写真の無い依頼には付かない）
  const ledger = rfq.show.photos ? (rfq.includePhotos && rfq.photos.total > 0 ? RFQ_MSG.confirmLedgerOn : RFQ_MSG.confirmLedgerOff) : null;
  const attachText = n ? RFQ_MSG.confirmAttach.replace("{n}", String(n)) : RFQ_MSG.confirmNoAttach;
  const how = [RFQ_MSG.viaMail, bcc ? RFQ_MSG.bccSelf : null].filter(Boolean).join("・");
  const rest = [attachText, ledger].filter(Boolean).join("・");
  const go = async () => {
    const v = RfqSendRequest.safeParse({ to, bccSelf: bcc, subject, body, mailMode: mode, ...(mode === "page" ? { expiresDays } : {}) });
    if (!v.success) {
      setErr(v.error.issues[0]?.message);
      // 件名・本文の誤りは閉じたままだと見えない
      if (v.error.issues.some((i) => i.path[0] === "subject" || i.path[0] === "body")) setMailOpen(true);
      return;
    }
    setBusy(true);
    setErr(undefined);
    try {
      const why = await beforeSend();
      if (why != null) {
        setErr(why);
        setBusy(false);
        return;
      }
      onSent(await api<Rfq>("POST", `/rfqs/${rfq.id}/send`, v.data));
    } catch (e) {
      setErr(messageOf(e));
      setBusy(false);
    }
  };
  const working = busy || outerBusy;
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && !working && onClose()}>
      <div className={"dlg rqsend" + (mailOpen ? " wide" : "")} role="dialog" aria-label={title} onKeyDown={(e) => e.key === "Escape" && !working && onClose()} data-testid="rfq-send-dialog">
        <div className="dh">{title}</div>
        <div className="db">
          <dl className="rqsend-dl">
            <dt>{RFQ_MSG.sendTo}</dt>
            <dd data-testid="rfq-confirm-to">
              <b>{who}</b>
              {known ? <span className="muted">{`（${known}）`}</span> : null}
            </dd>
            <dt>{RFQ_MSG.sendVia}</dt>
            <dd data-testid="rfq-confirm-how">{`${how}　${rest}`}</dd>
          </dl>
          <div className="rqsend-mode" data-testid="rfq-mail-mode-row">
            <span className="cllbl" id="rq_mode_l">
              {MAIL_MSG.mode}
            </span>
            <Segmented
              options={(["page", "attach"] as const).map((x) => ({ value: x, label: MAIL_MODE_LABEL[x], testId: `rfq-mail-mode-${x}` }))}
              value={mode}
              onChange={setMode}
              labelledBy="rq_mode_l"
              testId="rfq-mail-mode"
            />
            <Help label={MAIL_MSG.mode} text={MAIL_MSG.modeHelp} testId="help-rfq-mail-mode" />
            {mode === "page" ? (
              <span className="rqsend-exp">
                <span className="cllbl" id="rq_exp_l">
                  {MAIL_MSG.expires}
                </span>
                <NumInput kind="int" className="inp sm num" aria-labelledby="rq_exp_l" value={expiresDays} onChange={(n) => setExpiresDays(Math.min(MAIL_LIMITS.expiresMax, Math.max(MAIL_LIMITS.expiresMin, n ?? MAIL_LIMITS.expiresDefault)))} data-testid="rfq-mail-expires" />
                <span>{MAIL_MSG.expiresDays}</span>
                <span className="hint num">{MAIL_MSG.expiresUntil.replace("{d}", ymdShort(addDaysYmd(todayJst(), expiresDays)))}</span>
              </span>
            ) : null}
          </div>
          {!known ? (
            <Field id="rq_to" label={RFQ_MSG.mailTo}>
              <input id="rq_to" className="inp" type="email" autoComplete="off" autoFocus value={to} maxLength={254} onChange={(e) => setTo(e.target.value.trim())} data-testid="rfq-to" />
            </Field>
          ) : null}
          <button type="button" className="btn text rqsend-toggle" aria-expanded={mailOpen} aria-controls="rqsend-mail" onClick={() => setMailOpen(!mailOpen)} data-testid="rfq-mail-toggle">
            {RFQ_MSG.mailBodyToggle} <span className={"vqd-chev" + (mailOpen ? " is-open" : "")} aria-hidden="true" />
          </button>
          {mailOpen ? (
            <div id="rqsend-mail">
              <Field id="rq_msub" label={RFQ_MSG.mailSubject}>
                <input id="rq_msub" className="inp" value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} data-testid="rfq-mail-subject" />
              </Field>
              <Field id="rq_mbody" label={RFQ_MSG.mailBody}>
                <textarea id="rq_mbody" className="inp" rows={8} value={body} maxLength={4000} onChange={(e) => setBody(e.target.value)} data-testid="rfq-mail-body" />
              </Field>
            </div>
          ) : null}
          <ErrorText>{err}</ErrorText>
        </div>
        <div className="df">
          <button type="button" className="btn" disabled={working} onClick={onClose}>取消</button>
          <button type="button" className="btn primary" autoFocus={!!known} disabled={working || !to} onClick={() => void go()} data-testid="rfq-send-confirm">
            {working ? RFQ_MSG.sending : RFQ_MSG.sendDo}
          </button>
        </div>
      </div>
    </div>
  );
}
